import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
  index: number;
}
function FAQItem({
  question,
  answer,
  isOpen,
  onClick,
  index
}: FAQItemProps) {
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.4,
    delay: index * 0.1
  }} className="glass-card rounded-xl overflow-hidden" data-id="element-27">
      <button onClick={onClick} className="w-full flex items-center justify-between p-6 text-left" aria-expanded={isOpen} data-id="element-28">
        <span className="font-semibold text-white pr-4" data-id="element-29">{question}</span>
        <ChevronDownIcon className={`w-5 h-5 text-accent-green flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} data-id="element-30" />
      </button>
      <motion.div initial={false} animate={{
      height: isOpen ? 'auto' : 0,
      opacity: isOpen ? 1 : 0
    }} transition={{
      duration: 0.3
    }} className="overflow-hidden" data-id="element-31">
        <div className="px-6 pb-6 text-text-muted leading-relaxed" data-id="element-32">
          {answer}
        </div>
      </motion.div>
    </motion.div>;
}
export function FAQSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const faqs = [{
    question: 'Do I need any tech skills to use this system?',
    answer: 'Not at all. The system is designed for complete beginners. If you can use a web browser and follow step-by-step instructions, you can build a faceless YouTube channel. We walk you through every tool and process.'
  }, {
    question: 'Is this the same as YouTube Automation?',
    answer: 'No. YouTube Automation typically requires hiring teams and spending thousands upfront. The Anonymous Asset System teaches you to do everything yourself using AI tools, keeping your costs near zero while maintaining full control.'
  }, {
    question: 'How soon can I see results?',
    answer: 'You can have your first video uploaded within 7 days using our launch plan. Results vary, but many students see their first earnings within 2-4 weeks. Building sustainable income typically takes 2-3 months of consistent uploads.'
  }, {
    question: 'Does YouTube ban AI-generated content?',
    answer: "YouTube does not ban AI content. What matters is the value you provide to viewers. Our system teaches you to create genuinely helpful, engaging content that viewers love—regardless of how it's produced."
  }];
  return <section ref={ref} className="relative w-full py-24 md:py-32 px-4 bg-bg-primary" data-id="element-33">
      <div className="max-w-3xl mx-auto" data-id="element-34">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-12" data-id="element-35">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-36">
            Frequently Asked <span className="text-accent-cyan" data-id="element-37">Questions</span>
          </h2>
          <p className="text-text-muted text-lg" data-id="element-38">
            Got questions? We've got answers.
          </p>
        </motion.div>

        <div className="space-y-4" data-id="element-39">
          {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} isOpen={openIndex === index} onClick={() => setOpenIndex(openIndex === index ? null : index)} index={index} data-id="element-40" />)}
        </div>
      </div>
    </section>;
}